import { toast } from 'react-toastify';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Search, Users, ChevronDown, Upload, Download, ArrowUp, ArrowDown, X } from 'lucide-react';
import * as XLSX from 'xlsx';
import Pagination from 'react-responsive-pagination';
import 'react-responsive-pagination/themes/classic.css';
import AppLayout from '../components/AppLayout';
import Drawer from '../components/Drawer';
import ContactFormSlider from '../components/ContactFormSlider';
import ContactDetailsSlider from '../components/ContactDetailsSlider';
import ImportContactsModal from '../components/ImportContactsModal';
import { Contact, ContactFilters } from '../types/contact';
import { contactService } from '../services/contactService';
import '../styles/Contacts.css';
import '../styles/Buttons.css';

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const Contacts: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<ContactFilters>({});
  const [sortBy] = useState<'lastName'>('lastName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [formError, setFormError] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);
  const isFetchingRef = useRef(false);

  // Get drawer state from URL params
  const isDrawerOpen = searchParams.get('action') === 'new' || !!searchParams.get('edit');
  const editContactId = searchParams.get('edit');
  const viewContactId = searchParams.get('view');

  useEffect(() => {
    if (workspaceId) {
      // Initial load
      fetchContacts(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  useEffect(() => {
    if (workspaceId && !loading && !isFetchingRef.current) {
      // Data refresh for sort, pagination, search, filters
      fetchContacts(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, searchQuery, filters, sortOrder]);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchContacts = async (isDataRefresh: boolean = false) => {
    // Prevent duplicate calls
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;

      // Use dataLoading for data refreshes (sort, pagination), loading for initial load
      if (isDataRefresh) {
        setDataLoading(true);
      } else {
        setLoading(true);
      }
      setError('');

      const response = await contactService.getAll(
        workspaceId!,
        { ...filters, search: searchQuery },
        pagination.page,
        pagination.limit,
        sortBy,
        sortOrder
      );

      setContacts(response.contacts);

      // Only update pagination if values actually changed to avoid triggering useEffect
      const newPagination = response.pagination;
      if (
        newPagination.page !== pagination.page ||
        newPagination.limit !== pagination.limit ||
        newPagination.total !== pagination.total ||
        newPagination.pages !== pagination.pages
      ) {
        setPagination(newPagination);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch contacts');
      console.error('Error fetching contacts:', err);
    } finally {
      if (isDataRefresh) {
        setDataLoading(false);
      } else {
        setLoading(false);
      }
      isFetchingRef.current = false;
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleSort = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const getSortIcon = () => {
    return sortOrder === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />;
  };

  const handleCreateContact = () => {
    setSearchParams({ action: 'new' });
  };

  const handleImportClick = () => {
    setShowImportModal(true);
  };

  const handleImportClose = () => {
    setShowImportModal(false);
  };

  const handleImportSuccess = () => {
    setShowImportModal(false);
    fetchContacts();
  };

  const handleExportClick = () => {
    setShowExportDropdown(!showExportDropdown);
  };

  const exportToCSV = async () => {
    try {
      setIsExporting(true);
      setShowExportDropdown(false);

      // Fetch all contacts with current filters (no pagination limit)
      const response = await contactService.getAll(
        workspaceId!,
        { ...filters, search: searchQuery },
        1,
        pagination.total || 10000 // Get all contacts
      );

      const exportData = response.contacts.map(contact => ({
        'First Name': contact.firstName,
        'Middle Name': contact.middleName || '',
        'Last Name': contact.lastName,
        'Email': contact.emails.find(e => e.isPrimary)?.email || contact.emails[0]?.email || '',
        'Phone': contact.phones.find(p => p.isPrimary)?.number || contact.phones[0]?.number || '',
        'Contact Type': contact.contactType.join(', '),
        'Company': contact.company?.name || '',
        'Position': contact.company?.position || '',
        'Street Address': contact.addresses[0]?.street1 || '',
        'City': contact.addresses[0]?.city || '',
        'State': contact.geography?.state || contact.addresses[0]?.state || '',
        'Zip Code': contact.addresses[0]?.zip || '',
        'Country': contact.geography?.country || contact.addresses[0]?.country || '',
        'Status': contact.isInactive ? 'Inactive' : 'Active',
        'Labels': contact.labels?.join(', ') || '',
        'Notes': contact.internalNotes || ''
      }));

      // Convert to CSV
      const headers = Object.keys(exportData[0] || {});
      const csvContent = [
        headers.join(','),
        ...exportData.map(row =>
          headers.map(header => {
            const value = row[header as keyof typeof row];
            // Escape quotes and wrap in quotes if contains comma or newline
            const stringValue = String(value || '');
            if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          }).join(',')
        )
      ].join('\n');

      // Download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `contacts_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${response.contacts.length} contacts to CSV`);
    } catch (error: any) {
      console.error('Export error:', error);
      toast.error(error.response?.data?.message || 'Failed to export contacts');
    } finally {
      setIsExporting(false);
    }
  };

  const exportToXLSX = async () => {
    try {
      setIsExporting(true);
      setShowExportDropdown(false);

      // Fetch all contacts with current filters (no pagination limit)
      const response = await contactService.getAll(
        workspaceId!,
        { ...filters, search: searchQuery },
        1,
        pagination.total || 10000 // Get all contacts
      );

      const exportData = response.contacts.map(contact => ({
        'First Name': contact.firstName,
        'Middle Name': contact.middleName || '',
        'Last Name': contact.lastName,
        'Email': contact.emails.find(e => e.isPrimary)?.email || contact.emails[0]?.email || '',
        'Phone': contact.phones.find(p => p.isPrimary)?.number || contact.phones[0]?.number || '',
        'Contact Type': contact.contactType.join(', '),
        'Company': contact.company?.name || '',
        'Position': contact.company?.position || '',
        'Street Address': contact.addresses[0]?.street1 || '',
        'City': contact.addresses[0]?.city || '',
        'State': contact.geography?.state || contact.addresses[0]?.state || '',
        'Zip Code': contact.addresses[0]?.zip || '',
        'Country': contact.geography?.country || contact.addresses[0]?.country || '',
        'Status': contact.isInactive ? 'Inactive' : 'Active',
        'Labels': contact.labels?.join(', ') || '',
        'Notes': contact.internalNotes || ''
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const colWidths = [
        { wch: 15 }, // First Name
        { wch: 15 }, // Middle Name
        { wch: 15 }, // Last Name
        { wch: 25 }, // Email
        { wch: 15 }, // Phone
        { wch: 20 }, // Contact Type
        { wch: 20 }, // Company
        { wch: 20 }, // Position
        { wch: 30 }, // Street Address
        { wch: 15 }, // City
        { wch: 15 }, // State
        { wch: 10 }, // Zip Code
        { wch: 15 }, // Country
        { wch: 10 }, // Status
        { wch: 20 }, // Labels
        { wch: 30 }  // Notes
      ];
      ws['!cols'] = colWidths;

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Contacts');

      // Download
      XLSX.writeFile(wb, `contacts_export_${new Date().toISOString().split('T')[0]}.xlsx`);

      toast.success(`Exported ${response.contacts.length} contacts to Excel`);
    } catch (error: any) {
      console.error('Export error:', error);
      toast.error(error.response?.data?.message || 'Failed to export contacts');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, contact: Contact) => {
    e.stopPropagation();
    setContactToDelete(contact);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!contactToDelete) return;

    try {
      await contactService.delete(workspaceId!, contactToDelete._id);
      setShowDeleteModal(false);
      setContactToDelete(null);
      fetchContacts();
    } catch (err: any) {
      console.error('Error deleting contact:', err);
      toast.error(err.response?.data?.message || 'Failed to delete contact');
    }
  };

  const handleContactClick = (contact: Contact) => {
    setSearchParams({ view: contact._id });
  };

  const handleEditClick = (e: React.MouseEvent, contact: Contact) => {
    e.stopPropagation();
    setSearchParams({ edit: contact._id });
  };

  const handleCloseDrawer = () => {
    setSearchParams({});
    setFormError('');
  };

  const handleCloseDetails = () => {
    setSearchParams({});
  };

  const handleEditFromDetails = () => {
    if (viewContactId) {
      // Close details view and open edit form with the same contact ID
      setSearchParams({ edit: viewContactId });
    }
  };

  const handleFormSuccess = () => {
    setSearchParams({});
    fetchContacts();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleFilterChange = (key: keyof ContactFilters, value: any) => {
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, page: 1 });
  };

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page });
  };

  const handleLimitChange = (limit: number) => {
    setPagination({ ...pagination, limit, page: 1 }); // Reset to page 1 when changing limit
  };

  const getPrimaryEmail = (contact: Contact): string => {
    const primary = contact.emails.find(e => e.isPrimary);
    return primary?.email || contact.emails[0]?.email || '—';
  };

  const getPrimaryPhone = (contact: Contact): string => {
    const primary = contact.phones.find(p => p.isPrimary);
    return primary?.number || contact.phones[0]?.number || '—';
  };

  const getLocation = (contact: Contact): string => {
    if (!contact.geography?.state && !contact.geography?.country) return '—';
    const parts = [contact.geography?.state, contact.geography?.country].filter(Boolean);
    return parts.join(', ');
  };

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <div className="contacts-container">
        {/* Section Header */}
        <div className="contacts-section-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 className="contacts-section-title">Contacts</h2>
              <p className="contacts-section-subtitle">
                Manage buyers, agents, brokers and other contacts
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ position: 'relative' }} ref={exportDropdownRef}>
                <button
                  className="btn btn-outline btn-md"
                  onClick={handleExportClick}
                  disabled={isExporting || contacts.length === 0}
                >
                  <Download size={16} />
                  {isExporting ? 'Exporting...' : 'Export'}
                </button>
                {showExportDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '4px',
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    minWidth: '160px',
                    zIndex: 1000,
                    overflow: 'hidden'
                  }}>
                    <button
                      onClick={exportToCSV}
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        border: 'none',
                        background: 'white',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#374151',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      Export as CSV
                    </button>
                    <button
                      onClick={exportToXLSX}
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        border: 'none',
                        background: 'white',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#374151',
                        transition: 'background-color 0.2s',
                        borderTop: '1px solid #E5E7EB'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      Export as Excel
                    </button>
                  </div>
                )}
              </div>
              <button className="btn btn-outline btn-md" onClick={handleImportClick}>
                <Upload size={16} />
                Import Contacts
              </button>
              <button className="btn btn-primary btn-md" onClick={handleCreateContact}>
                + New Contact
              </button>
            </div>
          </div>
        </div>

        {/* White Card Container */}
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 2px 8px rgba(72, 24, 47, 0.08)'
        }}>
          {/* Actions Bar */}
          <div className="contacts-actions">
            <div className="contacts-search" style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={handleSearchChange}
                style={{ paddingRight: searchQuery ? '36px' : '16px' }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6B7280',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#374151'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#6B7280'}
                  aria-label="Clear search"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            <div className="contacts-filters">
              <select
                value={filters.isInactive !== undefined ? String(filters.isInactive) : ''}
                onChange={(e) => handleFilterChange('isInactive', e.target.value === '' ? undefined : e.target.value === 'true')}
              >
                <option value="">All Status</option>
                <option value="false">Active</option>
                <option value="true">Inactive</option>
              </select>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="form-error" style={{ marginBottom: '24px' }}>
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="contacts-loading">
              <div className="contacts-loading-spinner" />
            </div>
          )}

          {/* Empty State */}
          {!loading && contacts.length === 0 && !searchQuery && Object.keys(filters).length === 0 && (
            <div className="contacts-empty">
              <div className="contacts-empty-icon">
                <Users size={48} color="#9CA3AF" />
              </div>
              <h3 className="contacts-empty-title">No contacts yet</h3>
              <p className="contacts-empty-text">
                Get started by adding your first contact
              </p>
              <button className="btn btn-primary btn-lg" onClick={handleCreateContact}>
                Create your first contact
              </button>
            </div>
          )}

          {/* No Search Results */}
          {!loading && contacts.length === 0 && (searchQuery || Object.keys(filters).length > 0) && (
            <div className="contacts-empty">
              <div className="contacts-empty-icon">
                <Search size={48} color="#6B7280" />
              </div>
              <h3 className="contacts-empty-title">No contacts found</h3>
              <p className="contacts-empty-text">
                Try adjusting your search or filters
              </p>
            </div>
          )}

          {/* Contacts Table */}
          {!loading && contacts.length > 0 && (
            <>
              <div className="data-table-container" style={{ position: 'relative' }}>
                {/* Loading Overlay */}
                {dataLoading && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10
                  }}>
                    <div className="contacts-loading-spinner"></div>
                  </div>
                )}

                <table className="data-table" style={{ minWidth: '1200px' }}>
                  <thead>
                    <tr>
                      <th>
                        <button
                          onClick={handleSort}
                          className="sort-button"
                          disabled={dataLoading}
                        >
                          Name
                          <span className="sort-icon">
                            {getSortIcon()}
                          </span>
                        </button>
                      </th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Type</th>
                      <th>Location</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody style={{ opacity: dataLoading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                    {contacts.map((contact, index) => (
                      <tr
                        key={contact._id}
                        onClick={() => handleContactClick(contact)}
                        style={{
                          backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F5F3F0',
                          cursor: dataLoading ? 'wait' : 'pointer'
                        }}
                      >
                        <td className="data-table-primary-cell">
                          {contact.displayName}
                        </td>
                        <td>
                          {getPrimaryEmail(contact)}
                        </td>
                        <td>
                          {getPrimaryPhone(contact)}
                        </td>
                        <td>
                          <div className="contacts-types">
                            {contact.contactType.slice(0, 2).map((type, idx) => (
                              <span key={idx} className="contacts-type-badge">
                                {type}
                              </span>
                            ))}
                            {contact.contactType.length > 2 && (
                              <span className="contacts-type-badge contacts-type-more">
                                +{contact.contactType.length - 2}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          {getLocation(contact)}
                        </td>
                        <td>
                          <span className={`contacts-status-badge ${contact.isInactive ? 'contacts-status-inactive' : 'contacts-status-active'}`}>
                            {contact.isInactive ? 'Inactive' : 'Active'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination and Records Per Page */}
              <div className="contacts-pagination-controls">
                {/* Records Per Page Selector */}
                <div className="contacts-records-per-page">
                  <span>Show</span>
                  <select
                    value={pagination.limit}
                    onChange={(e) => handleLimitChange(Number(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span>records per page (Total: {pagination.total})</span>
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="contacts-pagination-wrapper">
                    <Pagination
                      current={pagination.page}
                      total={pagination.pages}
                      onPageChange={handlePageChange}
                      maxWidth={400}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && contactToDelete && (
        <div
          className="contacts-modal-overlay"
          onClick={() => setShowDeleteModal(false)}
        >
          <div className="contacts-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="contacts-modal-title">Delete Contact</h2>
            <p className="contacts-modal-subtitle">
              Are you sure you want to delete "{contactToDelete.displayName}"? This action cannot
              be undone.
            </p>
            <div className="contacts-modal-actions">
              <button
                className="btn btn-secondary btn-md"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-md"
                onClick={handleDeleteConfirm}
                style={{ background: '#DC2626' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Form Drawer */}
      <Drawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        title={editContactId ? 'Edit Contact' : 'Create New Contact'}
        error={formError}
        footer={
          <div className="drawer-footer-actions">
            <button
              type="button"
              className="btn btn-secondary btn-md"
              onClick={handleCloseDrawer}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-md"
              form="contact-form"
            >
              {editContactId ? 'Update Contact' : 'Create Contact'}
            </button>
          </div>
        }
      >
        {workspaceId && (
          <ContactFormSlider
            workspaceId={workspaceId}
            contactId={editContactId || undefined}
            onSuccess={handleFormSuccess}
            onCancel={handleCloseDrawer}
            onErrorChange={setFormError}
          />
        )}
      </Drawer>

      {/* Contact Details Slider */}
      {viewContactId && workspaceId && (
        <ContactDetailsSlider
          workspaceId={workspaceId}
          contactId={viewContactId}
          onClose={handleCloseDetails}
          onEdit={handleEditFromDetails}
          onSuccess={fetchContacts}
        />
      )}

      {/* Import Contacts Modal */}
      {showImportModal && workspaceId && (
        <ImportContactsModal
          workspaceId={workspaceId}
          onClose={handleImportClose}
          onSuccess={handleImportSuccess}
        />
      )}
    </AppLayout>
  );
};

export default Contacts;
